import type { HttpError } from "@shared/ui";
import { CalculatorError } from "../CalculatorError";
import type { AbstractResponseItem, MarketingFilter, ResultListResponse } from "./types";

export function resultListFilter<
	ResponseItem extends AbstractResponseItem,
	NewResponseItem extends AbstractResponseItem = ResponseItem,
>(
	results: ResultListResponse<ResponseItem> | ResponseItem[],
	filter: MarketingFilter = "normal",
	converter: (response: ResponseItem) => NewResponseItem = (r) => r as unknown as NewResponseItem,
): ResultListResponse<NewResponseItem> {
	let resultList: ResultListResponse<ResponseItem>;

	if (Array.isArray(results)) {
		resultList = makeResultList(results);
	} else {
		resultList = results;
	}

	const { highlightList: rawHighlightList = [], normalList: rawNormalList = [] } = resultList;
	const highlightList = rawHighlightList
		.filter((item) => !item.marketing?.excludeFromPublic)
		.map((item) => {
			const newItem = { ...item };

			if (!newItem.marketing) {
				newItem.marketing = { highlight: true };
			} else {
				newItem.marketing.highlight = true;
			}

			return converter(newItem);
		});
	const normalList = rawNormalList
		.filter((item) => !(item.marketing?.excludeFromPublic || item.marketing?.highlightOnly))
		.map((item) => {
			const newItem = { ...item };

			if (!newItem.marketing) {
				newItem.marketing = { highlight: false };
			}

			if (newItem.marketing?.highlight) {
				newItem.marketing.highlight = false;
			}

			// TODO: Ha megszünik a megállapodás ezt is törölni kell!
			if (
				filter !== "ct-only" &&
				newItem.marketing?.newTargetUrl &&
				(!["granit", "magnet"].includes(newItem.bank?.code) || filter === "without-ct")
			) {
				newItem.marketing.newTargetUrl = undefined;
			}

			return converter(newItem);
		});

	switch (filter) {
		case "ct-only":
			return {
				highlightList: highlightList.filter((item) => item.marketing?.newTargetUrl),
				normalList: normalList.filter((item) => item.marketing?.newTargetUrl),
			};
		case "without-ct":
			return {
				highlightList: [],
				normalList: normalList.filter((item) => !item.marketing?.newTargetUrl),
			};
		default:
			return {
				highlightList,
				normalList,
			};
	}
}

export function makeResultList<ResponseItem extends AbstractResponseItem>(
	responseItems: ResponseItem[],
): ResultListResponse<ResponseItem> {
	const highlightList: ResponseItem[] = [];
	const normalList: ResponseItem[] = [];
	responseItems.forEach((item: ResponseItem) => {
		if (item.marketing?.highlight) {
			highlightList.push(item);
		} else {
			// item.marketing = { highlight: false };
			normalList.push(item);
		}
	});

	return {
		highlightList,
		normalList,
	};
}

export function convertHttpErrorToCalculatorError(error: HttpError | CalculatorError): CalculatorError {
	let message = "Váratlan hiba történt a kalkuláció közben!";
	let fieldErrors: Record<string, string> = {};
	let needLog = true;

	if ("fieldErrors" in error) {
		return error;
	}

	if ("body" in error) {
		if (error.body?.message) {
			message = error.body?.message.split(".")[0];
		}

		if (error.body?.fieldErrors) {
			fieldErrors = error.body?.fieldErrors;
		}

		if (error.body?.ko && error.body?.ko === "JTM") {
			message = "A megadott jövedelem túl alacsony az igényelt hitelösszeghez viszonyítva.";
			needLog = false;
		}
	}

	return new CalculatorError(message, fieldErrors, error.request, error.response?.url, needLog);
}

export function catchCalculatorError(error: HttpError): any {
	throw convertHttpErrorToCalculatorError(error);
}
